import { useId, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import Autocomplete from "@mui/material/Autocomplete";
import MuiTextField from "@mui/material/TextField";
import { Pencil } from "lucide-react";

import { FormField } from "components/form/FormField/FormField";
import { Button } from "components/ui/Button";
import { HealthMailContactOption } from "features/results/components/HealthMailContactOption/HealthMailContactOption";
import { useHealthMailContactsList } from "features/results/hooks/useHealthMailContactsList";
import {
  filterContactOptions,
  formatContactLocation,
} from "features/results/utils/contactOrEmail";

import style from "./HealthMailContactOrEmailField.module.css";

const inputSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: "48px",
    borderRadius: "12px !important",
    backgroundColor: "#fdfdfd",
    fontFamily: "'Nunito', sans-serif",
    fontSize: "16px",
    border: "1px solid #8f8fa1",
    "& fieldset": { border: "none !important" },
    "&.Mui-focused": { border: "1px solid #006546" },
    "& .MuiAutocomplete-input": {
      padding: "0 !important",
      paddingLeft: "8px !important",
    },
  },
};

const LinkSummary = ({ name, detail, isEmpty, onEdit, labelId }) => (
  <div className={style.summary}>
    <div className={style.summaryText}>
      {isEmpty ? (
        <span className={style.muted}>Not set</span>
      ) : (
        <button
          type="button"
          className={style.link}
          onClick={onEdit}
          aria-describedby={labelId}
        >
          {name}
        </button>
      )}
      {!!detail && <span className={style.muted}>{detail}</span>}
    </div>
    <button
      type="button"
      className={style.change}
      onClick={onEdit}
      aria-describedby={labelId}
    >
      <Pencil size={14} aria-hidden />
      {isEmpty ? "Add" : "Change"}
    </button>
  </div>
);

/**
 * A HealthMail directory contact OR a typed email address, never both (KIN-2211).
 *
 * Collapsed it is one line, the chosen contact as a link with a Change button, so a
 * form carries it without giving it a section. Change opens the directory search and
 * the email input in place; choosing one clears the other, so the form never holds the
 * pair the API refuses. The directory is only fetched while it is open: a GP list is
 * thousands of rows, and most saves never touch this field.
 *
 * `initialContact` is the resolved contact from the record being edited. It names the
 * stored choice without the directory, and covers a stored contact outside `titles`.
 */
export const HealthMailContactOrEmailField = ({
  contactName,
  emailName,
  label,
  titles,
  initialContact,
  labelClassName,
}) => {
  const labelId = useId();
  const [isEditing, setIsEditing] = useState(false);
  const {
    control,
    setValue,
    register,
    watch,
    trigger,
    formState: { errors },
  } = useFormContext();

  // A refused submit opens the field, so the message is never hidden behind the link.
  const hasError = !!errors[contactName] || !!errors[emailName];
  const isOpen = isEditing || hasError;

  const { data: contacts, isLoading, isError } = useHealthMailContactsList(
    titles,
    { enabled: isOpen }
  );

  const contactId = watch(contactName);
  const email = watch(emailName);
  const options = contacts || [];
  const selectedContact =
    contactId == null
      ? null
      : options.find((contact) => contact.id === contactId) ??
        (initialContact?.id === contactId ? initialContact : null);

  const handleClear = () => {
    setValue(contactName, null, { shouldDirty: true });
    setValue(emailName, "", { shouldDirty: true });
  };

  const handleDone = async () => {
    const isValid = await trigger([contactName, emailName]);
    if (isValid) setIsEditing(false);
  };

  if (!isOpen) {
    const hasContact = contactId != null;

    return (
      <div className={style.field}>
        <span id={labelId} className={labelClassName}>
          {label}
        </span>
        <LinkSummary
          labelId={labelId}
          isEmpty={!hasContact && !email}
          name={hasContact ? selectedContact?.name || "Directory contact" : email}
          detail={hasContact ? formatContactLocation(selectedContact) : ""}
          onEdit={() => setIsEditing(true)}
        />
      </div>
    );
  }

  const emailField = register(emailName);

  return (
    <div className={`${style.field} ${style.fieldOpen}`}>
      <span id={labelId} className={labelClassName}>
        {label}
      </span>
      <div className={style.inputs}>
        <FormField name={contactName}>
          <Controller
            name={contactName}
            control={control}
            render={({ field }) => (
              <Autocomplete
                options={options}
                value={selectedContact}
                loading={isLoading}
                autoHighlight
                openOnFocus
                filterOptions={filterContactOptions}
                getOptionLabel={(contact) => contact?.name || ""}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                renderOption={(optionProps, contact) => (
                  <HealthMailContactOption
                    key={contact.id}
                    optionProps={optionProps}
                    contact={contact}
                  />
                )}
                onChange={(_, contact) => {
                  field.onChange(contact?.id ?? null);
                  if (contact) setValue(emailName, "", { shouldDirty: true });
                }}
                noOptionsText={
                  isError ? "Couldn't load the directory" : "No matching contacts"
                }
                renderInput={(params) => (
                  <MuiTextField
                    {...params}
                    placeholder="Search directory by name or county"
                    inputProps={{
                      ...params.inputProps,
                      "aria-labelledby": labelId,
                    }}
                    sx={inputSx}
                  />
                )}
              />
            )}
          />
        </FormField>
        <FormField name={emailName}>
          <input
            type="email"
            className="form-control"
            placeholder="Or enter an email address"
            aria-labelledby={labelId}
            {...emailField}
            onChange={(e) => {
              emailField.onChange(e);
              if (e.target.value.trim()) {
                setValue(contactName, null, { shouldDirty: true });
              }
            }}
          />
        </FormField>
      </div>
      <div className={style.actions}>
        <Button variant="textGray" size="small" onClick={handleClear}>
          Clear
        </Button>
        <Button variant="outline" size="small" onClick={handleDone}>
          Done
        </Button>
      </div>
    </div>
  );
};
